'use client';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import type { ImagePromptWithSize, ImageRange } from '@/app/types';
import useClipMask from './use-clip-mask';
import type { CropOption } from './use-crop-options';
import useCropOptions from './use-crop-options';
import { calculateRangeAndScale, getMaxCenteredRange, getNewRange } from './utils';

type UseImgCropControllerParams = {
  imageSource: ImagePromptWithSize | null;
  cropOptions?: CropOption[];
  defaultCropIndex?: number;
  /**
   * true 时：首次打开与切换比例都按所选比例在图内「居中最大化」，而非保持当前裁剪框短边。
   * 头像等「希望每次都占满可用区域」的场景开启；h5 模板保持默认 false（沿用旧交互）。
   */
  maximizeToAspect?: boolean;
  onConfirm: (range: ImageRange) => Promise<void>;
  onClose: () => void;
};

type UseImgCropControllerResult = {
  aspectRatios: CropOption[];
  selectedAspect: 'free' | number;
  saving: boolean;
  imgRef: React.RefObject<HTMLImageElement | null>;
  clipMaskDom: ReactNode;
  handleImageLoad: () => void;
  handleAspectChange: (value: 'free' | number) => void;
  handleSaveCropImage: () => Promise<void>;
  handleMoving: (event: React.PointerEvent) => void;
  handleMoveEnd: () => void;
  /** saving 期间不允许关闭。view 层关闭按钮/遮罩点击应先调这个判断。 */
  canClose: () => boolean;
};

/**
 * 裁剪弹窗的业务逻辑收敛点：range / scale / save / aspect 切换 / ResizeObserver / clipMask
 * 全部走这里。
 *
 * view 层（TemplateImgCropModal / AdmissionTemplateImgCropModal）只负责承载 UI（Dialog
 * vs portal bottom-sheet）和样式 token，不再各自维护一份业务 state。
 */
export function useImgCropController({
  imageSource,
  cropOptions,
  defaultCropIndex = 0,
  maximizeToAspect = false,
  onConfirm,
  onClose,
}: UseImgCropControllerParams): UseImgCropControllerResult {
  const [range, setRange] = useState<ImageRange>([0, 0, 0, 0]);
  const [saving, setSaving] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedAspect, setSelectedAspect] = useState<'free' | number>('free');

  const resizeScale = useRef<number>(1);
  const maxImageRange = useRef<ImageRange>([0, 0, 0, 0]);
  const imgRef = useRef<HTMLImageElement>(null);
  const handleImageLoad = () => setImageLoaded(true);

  const { freeAspectOption } = useCropOptions();

  const aspectRatios: CropOption[] = useMemo(() => {
    return cropOptions && cropOptions.length > 0 ? cropOptions : [freeAspectOption];
  }, [cropOptions]);

  useEffect(() => {
    if (!imageSource) {
      setImageLoaded(false);
      setRange([0, 0, 0, 0]);
      setSelectedAspect(aspectRatios[defaultCropIndex].value);
    }
  }, [imageSource, aspectRatios, defaultCropIndex]);

  useEffect(() => {
    if (!imageSource || !imgRef.current) return;

    const imgObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        const { sourceImageWidth, sourceImageHeight } = imageSource;
        if (!sourceImageWidth || !sourceImageHeight) return;

        let x1: number;
        let y1: number;
        let x2: number;
        let y2: number;

        const firstOption = aspectRatios[0];
        const defaultOption = aspectRatios[defaultCropIndex] ?? firstOption;
        if (imageSource.selectedImageRange) {
          // 二次打开：复用上次保存的裁剪区域，否则用户每次重开都丢失之前的选区。
          [x1, y1, x2, y2] = imageSource.selectedImageRange;
        } else if (maximizeToAspect && typeof defaultOption.value === 'number') {
          // 首次打开且要求最大化：按默认比例在图内居中最大化（多比例场景也生效）。
          [x1, y1, x2, y2] = getMaxCenteredRange(sourceImageWidth, sourceImageHeight, defaultOption.value);
        } else if (aspectRatios.length === 1 && typeof firstOption.value === 'number') {
          // 单一强制比例：首次打开按该比例居中最大化。
          [x1, y1, x2, y2] = getMaxCenteredRange(sourceImageWidth, sourceImageHeight, firstOption.value);
        } else {
          [x1, y1, x2, y2] = [0, 0, sourceImageWidth, sourceImageHeight];
        }

        const { scale, range: nextMaxRange } = calculateRangeAndScale(
          { width: sourceImageWidth, height: sourceImageHeight },
          { width, height },
        );
        resizeScale.current = scale;
        maxImageRange.current = nextMaxRange;

        setRange([
          x1 * resizeScale.current + maxImageRange.current[0],
          y1 * resizeScale.current + maxImageRange.current[1],
          x2 * resizeScale.current + maxImageRange.current[0],
          y2 * resizeScale.current + maxImageRange.current[1],
        ]);
      }
    });
    imgObserver.observe(imgRef.current);
    return () => {
      imgObserver.disconnect();
    };
  }, [imageLoaded]);

  const handleSaveCropImage = async () => {
    setSaving(true);
    const startPosition: [number, number] = [
      Math.floor((range[0] - maxImageRange.current[0]) / resizeScale.current),
      Math.floor((range[1] - maxImageRange.current[1]) / resizeScale.current),
    ];
    const endPosition: [number, number] = [
      Math.floor((range[2] - maxImageRange.current[0]) / resizeScale.current),
      Math.floor((range[3] - maxImageRange.current[1]) / resizeScale.current),
    ];
    try {
      await onConfirm([...startPosition, ...endPosition]);
      onClose();
    } finally {
      // onConfirm reject 时也必须恢复 saving，否则 canClose() 永久 false，弹窗卡死
      setSaving(false);
    }
  };

  const handleAspectChange = (value: 'free' | number) => {
    setSelectedAspect(value);
    if (value === 'free') return;
    if (maximizeToAspect && imageSource?.sourceImageWidth && imageSource?.sourceImageHeight) {
      // 切比例时按该比例在图内居中最大化，再换算到展示坐标系（含缩放与偏移）。
      const [sx1, sy1, sx2, sy2] = getMaxCenteredRange(
        imageSource.sourceImageWidth,
        imageSource.sourceImageHeight,
        value,
      );
      const [offsetX, offsetY] = maxImageRange.current;
      setRange([
        sx1 * resizeScale.current + offsetX,
        sy1 * resizeScale.current + offsetY,
        sx2 * resizeScale.current + offsetX,
        sy2 * resizeScale.current + offsetY,
      ]);
      return;
    }
    setRange(getNewRange(range, value, maxImageRange.current));
  };

  const { clipMaskDom, handleMoving, handleMoveEnd } = useClipMask({
    range,
    selectedAspect,
    maxImageRange,
    imageScale: resizeScale.current,
    setRange,
  });

  const canClose = () => !saving;

  return {
    aspectRatios,
    selectedAspect,
    saving,
    imgRef,
    clipMaskDom,
    handleImageLoad,
    handleAspectChange,
    handleSaveCropImage,
    handleMoving,
    handleMoveEnd,
    canClose,
  };
}
